import axios from "../utils/index";
import type {
  CredentialReviewStatus,
  JoinRequest,
  WorkforceState,
} from "../features/workforce/types";
import { resolveStorageDisplayUrl } from "../utils/uploadResponseUtils";

export type WorkforceSnapshot = {
  revision: number;
  state: WorkforceState;
};

const dataOf = <T>(response: { data?: { code?: number; data?: T; message?: string } }) => {
  if (response.data?.code === 200 && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data?.message || "Workforce request failed.");
};

export const getWorkforceState = async (): Promise<WorkforceSnapshot | null> => {
  try {
    const response = await axios.get("v1/hospital/workforce/state");
    if (response.data?.code !== 200 || !response.data?.data) return null;
    return response.data.data as WorkforceSnapshot;
  } catch {
    return null;
  }
};

export const putWorkforceState = async (
  snapshot: WorkforceSnapshot,
): Promise<WorkforceSnapshot> => {
  const response = await axios.put("v1/hospital/workforce/state", snapshot);
  return dataOf<WorkforceSnapshot>(response);
};

export const linkStatDoctorMember = async (body: {
  userId: string;
  employeeId?: string;
  departmentId?: string;
  positionId?: string;
  grade?: string;
  employmentType?: string;
  hourlyRate?: number;
  fte?: number;
}): Promise<WorkforceSnapshot> => {
  const response = await axios.post("v1/hospital/workforce/members/from-doctor", body);
  return dataOf<WorkforceSnapshot>(response);
};

export const publishWorkforcePeriod = async (body: {
  start?: string;
  end?: string;
  message?: string;
}): Promise<WorkforceSnapshot> => {
  const response = await axios.post("v1/hospital/workforce/publish", body);
  return dataOf<WorkforceSnapshot>(response);
};

export const listWorkforceClaims = async (): Promise<JoinRequest[]> => {
  try {
    const response = await axios.get("v1/hospital/workforce/claims");
    if (response.data?.code !== 200 || !Array.isArray(response.data?.data)) {
      return [];
    }
    return (response.data.data as unknown[])
      .filter((item): item is Record<string, unknown> =>
        Boolean(item) && typeof item === "object",
      )
      .map((item: Record<string, unknown>): JoinRequest => ({
        id: String(item.id || ""),
        firstName: String(item.firstName || ""),
        lastName: String(item.lastName || ""),
        designation: String(item.designation || ""),
        specialty: item.specialty != null ? String(item.specialty) : undefined,
        skillLevel: item.skillLevel != null ? String(item.skillLevel) : undefined,
        avatarUrl: item.avatarUrl != null
          ? resolveStorageDisplayUrl(String(item.avatarUrl)) || String(item.avatarUrl)
          : undefined,
        ahpra: item.ahpra != null ? String(item.ahpra) : undefined,
        message: String(item.message || ""),
        mobileUserId: String(item.mobileUserId || ""),
        createdAt: Number(item.createdAt || 0),
        status: "pending",
      }))
      .filter((item: JoinRequest) => Boolean(item.id));
  } catch {
    return [];
  }
};

export const acceptWorkforceClaim = async (
  claimId: string,
): Promise<WorkforceSnapshot> => {
  const response = await axios.post(
    `v1/hospital/workforce/claims/${encodeURIComponent(claimId)}/accept`,
  );
  return dataOf<WorkforceSnapshot>(response);
};

export const declineWorkforceClaim = async (
  claimId: string,
): Promise<WorkforceSnapshot> => {
  const response = await axios.post(
    `v1/hospital/workforce/claims/${encodeURIComponent(claimId)}/decline`,
  );
  return dataOf<WorkforceSnapshot>(response);
};

export type StaffLicenceDocument = {
  sourceKey: string;
  kind: string;
  name: string;
  url?: string;
  sourceValue?: string;
  present?: boolean;
  sourceUpdatedAt?: number;
  reviewStatus?: CredentialReviewStatus;
  expires?: string;
  expiresEnabled?: boolean;
  reviewedAt?: number;
  reviewedBy?: string;
  notes?: string;
  restrictionHas?: boolean;
  restrictionNote?: string;
  previousReviewStatus?: CredentialReviewStatus;
};

export type StaffLicence = {
  userId: string;
  staffId: string;
  ahpraNumber?: string;
  ahpraRestriction?: string;
  ahpraHasRestriction?: boolean;
  medicalUpdatedAt?: number;
  documents: StaffLicenceDocument[];
};

export type InternalDoctorProfile = {
  userId: string;
  staffId?: string;
  hospitalId?: string;
  hospitalName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  avatarUrl?: string;
  medicalDegree?: string;
  skillLevel?: string;
  skillLevelName?: string;
  specialties?: string;
  ahpraNumber?: string;
  ahpraHasRestriction?: boolean;
  ahpraRestriction?: string;
  designation?: string;
  grade?: string;
  employmentType?: string;
  departmentId?: string;
  completedShifts?: number;
  shortWork?: string;
  pending?: boolean;
};

export const getInternalDoctorProfile = async (
  userId: string,
): Promise<InternalDoctorProfile> => {
  const response = await axios.get(
    `v1/hospital/workforce/members/${encodeURIComponent(userId)}/profile`,
  );
  return dataOf<InternalDoctorProfile>(response);
};

export const removeInternalDoctor = async (
  userId: string,
): Promise<WorkforceSnapshot> => {
  const response = await axios.delete(
    `v1/hospital/workforce/members/${encodeURIComponent(userId)}`,
  );
  return dataOf<WorkforceSnapshot>(response);
};

export const getStaffLicence = async (
  userId: string,
): Promise<StaffLicence> => {
  const response = await axios.get(
    `v1/hospital/workforce/members/${encodeURIComponent(userId)}/licence`,
  );
  return dataOf<StaffLicence>(response);
};

export const refreshStaffLicence = async (
  userId: string,
): Promise<WorkforceSnapshot> => {
  const response = await axios.post(
    `v1/hospital/workforce/members/${encodeURIComponent(userId)}/licence/refresh`,
  );
  return dataOf<WorkforceSnapshot>(response);
};
